import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { EEMonitoringProgress } from "../../../../../Component/Model/Dto/EmploymentEquity/Plan/EEMonitoringProgress";
import { yupResolver } from "@hookform/resolvers/yup";
import { monitoringProgressSchema } from "./MonitoringProgressValidation";
import { toast } from "react-toastify";
import { Box, Dialog, DialogActions, DialogContent, FormHelperText, Grid } from "@mui/material";
import { CustomDialogTitle } from "../../../../Common/Custom/CustomDialogTitle";
import { MDBox } from "../../../../../Component/Common/Components/MDBox";
import { MDTypography } from "../../../../../Component/Common/Components/MDTypography";
import { Quill } from "../../../../../Component/Common/Components/Quill/Quill";
import { NotesTable } from "../../../../Common/Notes/NotesTable";
import { LoadingButton } from "@mui/lab";
import { MonitoringProgressService } from "../../../../../Component/Api/EmploymentEquity/Plan/MonitoringProgress/MonitoringProgressService";
import { UserActivityService } from "../../../../../Component/Api/UserActivity/UserActivityService";
import { MonitoringProgressSection } from "../../../../../Component/Common/Constants/UserActivityTrackingSections";
import { UserActivityActionType } from "../../../../../Component/Model/Enum/BEE123Enums";

interface IProps {
    id?: string;
    scorecardId: string;
    link: string;
    previewPopup: boolean;
    handleCancel: () => void;
    refreshTable: () => void;
    setMonitoringProgressId: (id: string) => void;
}

export const MonitoringProgressModal = (props: IProps) => {
    
    const [monitoringProgressId, setMonitoringProgressId] = useState<string | undefined>(props.id);

    const stakeholderMaxLength: number = 100;
    const responsibilityMaxLength: number = 600;
    const frequencyMaxLength: number = 600;

    const [remainingStakeholderCharacters, setRemainingStakeholderCharacters] = useState(stakeholderMaxLength);
    const [remainingResponsibilityCharacters, setRemainingResponsibilityCharacters] = useState(responsibilityMaxLength);
    const [remainingFrequencyCharacters, setRemainingFrequencyCharacters] = useState(frequencyMaxLength);

    const scorecardElement = 22;
    const noteCaptureSheet = 38;

    const { control, reset, handleSubmit, formState: { errors, isSubmitting }, setValue, getValues } = useForm<EEMonitoringProgress.DtoOut>({
        mode: 'all',
        defaultValues: {
            scorecardId: props.scorecardId,
        },
        resolver: yupResolver(monitoringProgressSchema)
    });

    const monitoringProgressLog = (actionType: number, actionDescription: string) => {
        UserActivityService.createActivityLog({
            section: MonitoringProgressSection,
            actionType: actionType,
            actionDescription: actionDescription,
            scorecardId: props.scorecardId,
            requestUrl: window.location.href
        });
    }

    const handleDataSubmit: SubmitHandler<EEMonitoringProgress.DtoOut> = async (monitoringProgress: EEMonitoringProgress.DtoOut) => {      
        try {
            if (!props.id) {
                const result = await MonitoringProgressService.CreateMonitoringProgress(monitoringProgress);
                monitoringProgressLog(UserActivityActionType.Create, "Add a progress entry");
                toast.success("Monitoring progress added succesfully");
                const savedId = result.data.id;
                setMonitoringProgressId(savedId);
                setValue("id", savedId);
                props.setMonitoringProgressId(savedId);
            }

            else {
                await MonitoringProgressService.UpdateMonitoringProgress(monitoringProgress);
                monitoringProgressLog(UserActivityActionType.Update, "Edit a progress entry");
                toast.success("Monitoring progress saved succesfully")
            }

            props.refreshTable();

        } catch (error) {
            console.log(error)
        }

    }

    const handleCancel = () => {
        props.handleCancel();
        props.refreshTable();
    }

    const getMonitoringProgress = async (id: string) => {
        try {
            let result = await MonitoringProgressService.GetMonitoringProgress(id);
            var monitoringProgress = 
                {
                    id: result.data.id,
                    scorecardId: result.data.scorecardId,
                    stakeholder: result.data.stakeholder,
                    responsibility: result.data.responsibility,
                    frequency: result.data.frequency
                } as EEMonitoringProgress.DtoOut
            reset(monitoringProgress)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        if (monitoringProgressId) {
            getMonitoringProgress(monitoringProgressId);
        }

    }, []);

    return (
        <Box >
            <Dialog open={props.previewPopup} onClose={handleCancel} aria-labelledby="form-dialog-title" maxWidth='md' fullWidth>
                <CustomDialogTitle id="customized-dialog-title" onClose={handleCancel}>
                    {props.id !== undefined ? "Edit" : "Add"}
                </CustomDialogTitle>
                <DialogContent>
                    <Grid container justifyContent='center' sx={{ my: 2 }}>
                        <Grid item xs={10} lg={10}>
                            <Box component="form"  id='MonitoringProgressForm' onSubmit={handleSubmit(handleDataSubmit)}>
                                <MDBox>
                                    <MDBox mb={1}>
                                        <Grid item xs={12}>

                                            <MDBox mb={2} className="leftAlign">
                                                <MDTypography
                                                    variant='button'
                                                    fontWeight='regular'
                                                    style={{ color: '#666666', fontSize: "0.85em" }} >
                                                    Stakeholder* (Max 100 characters)
                                                </MDTypography>

                                                <Controller
                                                    control={control}
                                                    name="stakeholder"
                                                    render={({ field: { onChange, value } }) => (
                                                        <Quill
                                                            initialContent={value}
                                                            maxLength={stakeholderMaxLength}
                                                            handleChange={(newValue: string) => {
                                                                onChange(newValue);
                                                                setValue('stakeholder', newValue);
                                                            }}
                                                            setRemainingCharacters={setRemainingStakeholderCharacters}
                                                        />
                                                )}
                                            />
                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="regular"
                                                    style={{  color:
                                                        remainingStakeholderCharacters < 0
                                                       ? "#FF0000" 
                                                       : "#666666", 
                                                        fontSize: "0.85em"
                                                    }}
                                                >
                                                    {remainingStakeholderCharacters} characters remaining
                                                </MDTypography>
                                                {(!!errors.stakeholder) && <FormHelperText error>{errors.stakeholder?.message}</FormHelperText>}
                                            </MDBox>

                                            <MDBox mb={2} className="leftAlign">
                                                <MDTypography
                                                    variant='button'
                                                    fontWeight='regular'
                                                    style={{ color: '#666666', fontSize: "0.85em" }}
                                                >
                                                    Role / Responsibility* (Max 600 characters)
                                                </MDTypography>
                                                <Controller
                                                    control={control}
                                                    name="responsibility"
                                                    render={({ field: { onChange, value } }) => (
                                                        <Quill
                                                            initialContent={value}
                                                            maxLength={responsibilityMaxLength}
                                                            handleChange={(newValue: string) => {
                                                                onChange(newValue);
                                                                setValue('responsibility', newValue);
                                                            }}
                                                            setRemainingCharacters={setRemainingResponsibilityCharacters}
                                                        />
                                                    )}
                                            />
                                            <MDTypography
                                                variant="button"
                                                fontWeight="regular"
                                                style={{  color:
                                                    remainingResponsibilityCharacters < 0
                                                   ? "#FF0000" 
                                                   : "#666666", 
                                                    fontSize: "0.85em"
                                                }}
                                            >
                                                {remainingResponsibilityCharacters} characters remaining
                                            </MDTypography>
                                                {(!!errors.responsibility) && <FormHelperText error>{errors.responsibility?.message}</FormHelperText>}
                                            </MDBox>

                                            <MDBox mb={2} className="leftAlign">
                                                <MDTypography
                                                    variant='button'
                                                    fontWeight='regular'
                                                    style={{ color: '#666666', fontSize: "0.85em" }}
                                                >
                                                    Frequency* (Max 600 characters)
                                                </MDTypography>
                                                <Controller
                                                    control={control}
                                                    name="frequency"
                                                    render={({ field: { onChange, value } }) => (
                                                        <Quill
                                                            initialContent={value}
                                                            maxLength={frequencyMaxLength}
                                                            handleChange={(newValue: string) => {
                                                                onChange(newValue);
                                                                setValue('frequency', newValue);
                                                            }}
                                                            setRemainingCharacters={setRemainingFrequencyCharacters}
                                                        />
                                                )}
                                            />
                                            <MDTypography
                                                variant="button"
                                                fontWeight="regular"
                                                style={{  color:
                                                    remainingFrequencyCharacters < 0
                                                   ? "#FF0000" 
                                                   : "#666666", 
                                                    fontSize: "0.85em"
                                                }}
                                            >
                                                {remainingFrequencyCharacters} characters remaining
                                            </MDTypography>
                                                {(!!errors.frequency) && <FormHelperText error>{errors.frequency?.message}</FormHelperText>}
                                            </MDBox>

                                        </Grid>
                                    </MDBox>
                                </MDBox>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <LoadingButton variant='contained' color='primary' type='submit' form='MonitoringProgressForm' loading={isSubmitting}>
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>);
}